/**
 * B935 (TRA-912) — a modal holds the keyboard.
 *
 * Measured on dev 2026-10-20 (a Tab walk at 1440): the house `Dialog` left
 * the focus on its opener, and every Tab stop after it was a door BEHIND the
 * overlay — covered, so a pointer could not reach it, while the keyboard
 * walked straight through (the operator's LOG IN AS: 13 of 13 stops) until
 * the focus fell to `<body>`. The `RightSheet` took the focus but let Tab
 * out after its last field onto the page's own Edit doors under the scrim.
 *
 * The rule both house modals compose:
 * - opening moves the focus into the modal (the panel itself, `tabIndex -1`,
 *   so the title is what is read first) unless a stop inside already has it;
 * - Tab from the last stop wraps to the first, Shift+Tab from the first (or
 *   from the panel) wraps to the last, and a focus that strayed outside comes
 *   back in — between inner stops the browser's own order is left alone;
 * - Escape calls `onClose` (the same as ✕ — the CALLER decides whether that
 *   closes or asks first), unless an inner control already answered it;
 * - closing hands the focus back to the opener, when the opener is still in
 *   the document and the focus has nowhere better to be;
 * - with one modal over another (a "discard?" Dialog over a sheet) only the
 *   TOP one — the last `aria-modal` in the document — answers Tab and Escape.
 */
import { useEffect, useRef, type RefObject } from "react";
import { focusIsLost } from "./viewFocus.js";

/** What counts as an open house modal. */
export const MODAL_SELECTOR = '[role="dialog"][aria-modal="true"]';

const TABBABLE = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  '[contenteditable="true"]',
  "[tabindex]",
].join(", ");

const shown = (el: HTMLElement): boolean => {
  if (el.closest("[hidden], [inert]") !== null) return false;
  const check = (el as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === "function" ? check.call(el) : true;
};

/** The modal's Tab stops in document order (what the browser would walk). */
export function modalStops(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => el.tabIndex >= 0 && shown(el),
  );
}

/** True when no other open modal sits above this one. */
export function isTopModal(panel: HTMLElement): boolean {
  const all = panel.ownerDocument.querySelectorAll(MODAL_SELECTOR);
  return all[all.length - 1] === panel;
}

/**
 * Attach the returned ref to the modal's panel (the `role="dialog"`
 * element, which carries `tabIndex={-1}`); pass whether it is open and its
 * close handler.
 */
export function useModalFocus<T extends HTMLElement = HTMLElement>(open: boolean, onClose: () => void): RefObject<T> {
  const ref = useRef<T>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const panel = ref.current;
    if (!open || panel === null) return undefined;
    const doc = panel.ownerDocument;
    /* No `instanceof HTMLElement`: some app tests render through a fake
       document with no such global (b912PoolMatch). */
    const before = doc.activeElement as HTMLElement | null;
    const opener =
      before !== null && typeof before.focus === "function" && !focusIsLost(doc) && !panel.contains(before)
        ? before
        : undefined;
    if (!panel.contains(doc.activeElement)) panel.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (!isTopModal(panel)) return;
      if (e.key === "Escape") {
        if (!e.defaultPrevented) close.current();
        return;
      }
      if (e.key !== "Tab") return;
      const stops = modalStops(panel);
      const active = doc.activeElement;
      const inside = active !== null && panel.contains(active);
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) {
        e.preventDefault();
        panel.focus();
        return;
      }
      if (e.shiftKey ? !inside || active === first || active === panel : !inside || active === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    doc.addEventListener("keydown", onKey);
    return () => {
      doc.removeEventListener("keydown", onKey);
      if (opener === undefined || !opener.isConnected) return;
      if (focusIsLost(doc) || panel.contains(doc.activeElement)) opener.focus();
    };
  }, [open]);
  return ref;
}
